/**
 * useSerialQueries — orchestrates N independent dashboard widget fetches with
 * a concurrency cap and per-widget status.
 *
 * Why this exists:
 *   Dashboard-style pages (Entities, Dashboard, IntelligenceHub) used to fire
 *   5+ parallel fetches on mount. When one endpoint is slow (e.g. the
 *   entity stats query), axios timed out (default 30s) on ALL of them because
 *   they shared the same cold-cache stampede on the server. This hook:
 *
 *   1. Caps concurrency (default 2) so slow calls don't starve the pool.
 *   2. Tracks per-key status so a single widget can show loading/error/retry
 *      while its neighbours keep rendering.
 *   3. Honours optional per-query timeout (caller overrides axios default).
 *   4. Aborts in-flight requests on unmount via AbortController.
 *   5. Optional periodic refetch — runs through the same concurrency queue,
 *      so background refreshes don't stampede either.
 *
 * Deliberately simple. Not a React Query replacement — no cache, no dedup
 * across hook instances. If two components need the same data, hoist it.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type QueryStatus = 'idle' | 'loading' | 'ok' | 'error' | 'timeout';

export interface SerialQuery<T = unknown> {
  /** Stable identifier used as a key in the returned maps. */
  key: string;
  /** The fetch function. Receives an AbortSignal wired to the hook's lifetime. */
  fn: (signal: AbortSignal) => Promise<T>;
  /** Optional per-query timeout (ms). Default: no additional timeout layered on top of axios. */
  timeout?: number;
}

export interface UseSerialQueriesOptions {
  /** Max concurrent in-flight queries. Default 2. */
  concurrency?: number;
  /** If set, the whole batch is re-run every refetchInterval ms. */
  refetchInterval?: number;
  /** Called once the initial batch has finished (regardless of individual results). */
  onSettled?: () => void;
  /** If false, skips the initial run (for conditional fetching). Default true. */
  enabled?: boolean;
}

export interface UseSerialQueriesResult<T = unknown> {
  /** Per-key result. Undefined until the query resolves ok. */
  results: Record<string, T | undefined>;
  /** Per-key lifecycle status. */
  statuses: Record<string, QueryStatus>;
  /** Per-key error message. Undefined when status !== 'error' | 'timeout'. */
  errors: Record<string, string | undefined>;
  /** Re-run all queries (respects the concurrency cap). */
  refetchAll: () => void;
  /** Re-run a single query by key. */
  retry: (key: string) => void;
  /** True while any query is loading. */
  isLoading: boolean;
}

function isTimeoutError(err: unknown): boolean {
  if (!err) return false;
  const any = err as { code?: string; message?: string; name?: string };
  if (any.code === 'ECONNABORTED') return true;
  if (any.name === 'AbortError') return false; // deliberate abort, not a timeout
  const msg = String(any.message || '').toLowerCase();
  return msg.includes('timeout');
}

export function useSerialQueries<T = unknown>(
  queries: SerialQuery<T>[],
  options: UseSerialQueriesOptions = {}
): UseSerialQueriesResult<T> {
  const { concurrency = 2, refetchInterval, onSettled, enabled = true } = options;

  // Freeze the query list on mount. We do NOT react to changes in `queries`
  // because callers inline the array literal — that would cause infinite
  // re-runs. If a caller needs to change the set, they should remount.
  const queriesRef = useRef(queries);
  // Keep fn/timeout in sync so callers can close over fresh state. key set
  // is assumed stable.
  queriesRef.current = queries;

  const [results, setResults] = useState<Record<string, T | undefined>>(() => {
    const seed: Record<string, T | undefined> = {};
    for (const q of queries) seed[q.key] = undefined;
    return seed;
  });
  const [statuses, setStatuses] = useState<Record<string, QueryStatus>>(() => {
    const seed: Record<string, QueryStatus> = {};
    for (const q of queries) seed[q.key] = 'idle';
    return seed;
  });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});

  const abortersRef = useRef<Record<string, AbortController | undefined>>({});
  const mountedRef = useRef(true);

  const runOne = useCallback(async (key: string) => {
    const q = queriesRef.current.find((x) => x.key === key);
    if (!q) return;

    // Cancel any previous inflight request for this key.
    abortersRef.current[key]?.abort();
    const ctrl = new AbortController();
    abortersRef.current[key] = ctrl;

    if (!mountedRef.current) return;
    setStatuses((s) => ({ ...s, [key]: 'loading' }));
    setErrors((e) => ({ ...e, [key]: undefined }));

    // Optional soft timeout layered on top of whatever axios is doing.
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    if (q.timeout && q.timeout > 0) {
      timeoutId = setTimeout(() => ctrl.abort(), q.timeout);
    }

    try {
      const value = await q.fn(ctrl.signal);
      if (!mountedRef.current || ctrl.signal.aborted) return;
      setResults((r) => ({ ...r, [key]: value }));
      setStatuses((s) => ({ ...s, [key]: 'ok' }));
    } catch (err) {
      if (!mountedRef.current) return;
      // Distinguish a deliberate unmount abort from a timeout abort.
      if (ctrl.signal.aborted && !isTimeoutError(err)) {
        // Silent — caller unmounted or retried.
        return;
      }
      const msg = (err as Error)?.message || String(err);
      setStatuses((s) => ({ ...s, [key]: isTimeoutError(err) || ctrl.signal.aborted ? 'timeout' : 'error' }));
      setErrors((e) => ({ ...e, [key]: msg }));
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
      if (abortersRef.current[key] === ctrl) {
        abortersRef.current[key] = undefined;
      }
    }
  }, []);

  const runBatch = useCallback(async (keys: string[]) => {
    // Simple fixed-concurrency pool: launch up to N at a time, as one
    // finishes, start the next.
    const queue = keys.slice();
    const inflight: Promise<void>[] = [];

    const launchNext = (): Promise<void> | null => {
      const key = queue.shift();
      if (!key) return null;
      const p = runOne(key).finally(() => {
        const nxt = launchNext();
        if (nxt) inflight.push(nxt);
      });
      return p;
    };

    for (let i = 0; i < Math.max(1, concurrency); i++) {
      const p = launchNext();
      if (p) inflight.push(p);
    }

    await Promise.allSettled(inflight);
    if (mountedRef.current) onSettled?.();
  }, [concurrency, runOne, onSettled]);

  const refetchAll = useCallback(() => {
    const keys = queriesRef.current.map((q) => q.key);
    void runBatch(keys);
  }, [runBatch]);

  const retry = useCallback((key: string) => {
    void runOne(key);
  }, [runOne]);

  // Initial batch + optional interval.
  useEffect(() => {
    mountedRef.current = true;
    if (!enabled) return () => { mountedRef.current = false; };

    refetchAll();

    let intervalId: ReturnType<typeof setInterval> | undefined;
    if (refetchInterval && refetchInterval > 0) {
      intervalId = setInterval(refetchAll, refetchInterval);
    }

    return () => {
      mountedRef.current = false;
      if (intervalId) clearInterval(intervalId);
      // Abort any inflight requests on unmount.
      for (const k of Object.keys(abortersRef.current)) {
        abortersRef.current[k]?.abort();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refetchInterval]);

  const isLoading = useMemo(
    () => Object.values(statuses).some((s) => s === 'loading'),
    [statuses],
  );

  return { results, statuses, errors, refetchAll, retry, isLoading };
}
